import { useState, useEffect } from 'react';
import {
  CheckCircle2, XCircle, Loader2, Sparkles,
  Plus, Trash2, GripVertical, Pencil, Power, PowerOff,
  Type, Image, Shield, Volume2, RefreshCw, AlertTriangle, CloudUpload,
} from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import AiProviderFormModal, {
  PROVIDER_TYPES,
  PROVIDER_META,
  buildAiProviderPayload,
  type AiProviderFormValue,
} from './AiProviderFormModal';
import { useUIStore } from '@/stores/ui-store';
import { useAuthStore } from '@/stores/auth-store';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AiProvider {
  id: string;
  name: string;
  slug: string;
  provider_type: string;
  capabilities: string[];
  api_base_url: string | null;
  account_id: string | null;
  api_key_masked: string;
  has_api_key: boolean;
  model_config: Record<string, any>;
  text_priority: number;
  image_priority: number;
  is_active: boolean;
  // Carried so the edit modal pre-fills the tier; without it, saving an edit
  // would reset a 'paid' row to 'free' (the form always sends cost_tier).
  cost_tier?: string;
  // Live (non-deprecated) models in the registry for this provider's catalog.
  // null when the count could not be read — render nothing rather than lie
  // with a zero, which is the one value that means something here.
  model_count?: number | null;
}

interface PlatformFallbackInfo {
  provider_type: string;
  capabilities: string[];
  has_text_model: boolean;
  has_image_model: boolean;
}

type TestStatus = 'idle' | 'testing' | 'success' | 'error';
type TabType = 'text' | 'image' | 'audio';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AiSettingsSection() {
  const [providers, setProviders] = useState<AiProvider[]>([]);
  const [platformFallbacks, setPlatformFallbacks] = useState<PlatformFallbackInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('text');
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState<AiProvider | null>(null);
  const [saving, setSaving] = useState(false);
  const [testStatuses, setTestStatuses] = useState<Record<string, TestStatus>>({});
  const [testErrors, setTestErrors] = useState<Record<string, string>>({});
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const addToast = useUIStore((s) => s.addToast);
  // Platform admins can hand a site's copy of a Rumio key over to the metered platform pool.
  const isPlatformAdmin = useAuthStore((s) => s.user?.isPlatformAdmin ?? false);
  const [moving, setMoving] = useState<string | null>(null);

  useEffect(() => {
    // Auto-run the connection test for every provider on load so the status
    // dots reflect real key validity at a glance — a silently-invalid key shows
    // red without clicking Test on each row.
    loadProviders().then((ps) => ps.forEach((p) => void handleTestConnection(p.id)));
  }, []);

  async function loadProviders(): Promise<AiProvider[]> {
    try {
      setLoading(true);
      // /platform-fallbacks returns metadata only (provider_type + capability flags) —
      // never an api_key, name, or any platform-side identifier.
      const [provRes, fbRes] = await Promise.all([
        api.get<{ providers: AiProvider[] }>('/admin/ai/providers'),
        api.get<{ fallbacks: PlatformFallbackInfo[] }>('/admin/ai/platform-fallbacks').catch(() => ({ fallbacks: [] })),
      ]);
      setProviders(provRes.providers || []);
      setPlatformFallbacks(fbRes.fallbacks || []);
      return provRes.providers || [];
    } catch (err) {
      console.error('Failed to load AI providers:', err);
      addToast('Failed to load AI providers', 'error');
      return [];
    } finally {
      setLoading(false);
    }
  }

  // Re-test every provider (header "Recheck keys" button).
  function recheckAll() {
    providers.forEach((p) => void handleTestConnection(p.id));
  }

  // For the active tab, return the set of provider_types where a platform
  // fallback is available with a configured model for this capability.
  function fallbackTypesForActiveTab(): Set<string> {
    const set = new Set<string>();
    for (const fb of platformFallbacks) {
      if (!fb.capabilities.includes(activeTab)) continue;
      const hasModel = activeTab === 'text' ? fb.has_text_model : fb.has_image_model;
      if (hasModel) set.add(fb.provider_type);
    }
    return set;
  }

  // Get providers filtered and sorted for the active tab. Audio piggybacks
  // on text_priority — matches the backend ai-registry decision to reuse
  // the text ordering column for audio (no separate audio_priority).
  function getTabProviders(): AiProvider[] {
    return providers
      .filter((p) => p.capabilities.includes(activeTab))
      .sort((a, b) => {
        const aPriority = activeTab === 'image' ? a.image_priority : a.text_priority;
        const bPriority = activeTab === 'image' ? b.image_priority : b.text_priority;
        return bPriority - aPriority; // DESC
      });
  }

  function openAddDialog() {
    setEditing(null);
    setShowDialog(true);
  }

  function openEditDialog(provider: AiProvider) {
    setEditing(provider);
    setShowDialog(true);
  }

  async function handleSubmit(value: AiProviderFormValue) {
    setSaving(true);
    try {
      const payload = buildAiProviderPayload(value, { editing: !!editing });

      let response: {
        provider?: { auto_discovered?: { refreshed: boolean; model_count: number; derived_capabilities: string[] } };
      };
      if (editing) {
        response = await api.put(`/admin/ai/providers/${editing.id}`, payload);
      } else {
        // Capabilities are discovered server-side, so we don't know them at
        // submit time — assign the next slot in BOTH the text/audio and image
        // failover sequences. The provider only surfaces in the tabs its
        // discovered caps populate, so a stray priority on an unused capability
        // is never read. (Audio shares the text_priority column, matching the
        // ai-registry column-reuse decision.)
        const textOrAudioProviders = providers.filter(
          (p) => p.capabilities.includes('text') || p.capabilities.includes('audio'),
        );
        const imageProviders = providers.filter((p) => p.capabilities.includes('image'));
        const nextTextPriority = textOrAudioProviders.length > 0
          ? Math.max(...textOrAudioProviders.map((p) => p.text_priority)) + 10
          : 10;
        const nextImagePriority = imageProviders.length > 0
          ? Math.max(...imageProviders.map((p) => p.image_priority)) + 10
          : 10;
        response = await api.post('/admin/ai/providers', {
          ...payload,
          text_priority: nextTextPriority,
          image_priority: nextImagePriority,
        });
      }

      const auto = response.provider?.auto_discovered;
      if (auto) {
        if (auto.refreshed && auto.model_count > 0) {
          const caps = auto.derived_capabilities.length > 0 ? auto.derived_capabilities.join(', ') : 'none';
          addToast(`${editing ? 'Updated' : 'Added'} — discovered ${auto.model_count} models (${caps})`, 'success');
        } else {
          // Was a 'success' toast promising the hourly cron would sort it out.
          // For DeepSeek that was permanently untrue — the cron retried every
          // hour and got zero every time, because the fetcher resolved no base
          // URL — and the reassurance is what stopped anyone looking. A retry
          // is worth mentioning; it is not worth calling a success.
          addToast(
            `${editing ? 'Updated' : 'Added'}, but no models were discovered — this provider cannot serve requests yet. ` +
              `The hourly refresh will retry; if it still shows "No models", the key or the catalog fetch is failing.`,
            'warning',
          );
        }
      } else {
        addToast(`Provider ${editing ? 'updated' : 'added'}`, 'success');
      }
      setShowDialog(false);
      await loadProviders();
    } catch (err: any) {
      addToast(err.message || 'Failed to save provider', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this AI provider?')) return;
    try {
      await api.delete(`/admin/ai/providers/${id}`);
      addToast('Provider deleted', 'success');
      await loadProviders();
    } catch {
      addToast('Failed to delete provider', 'error');
    }
  }

  async function handleMoveToPlatform(provider: AiProvider) {
    const ok = window.confirm(
      `Move "${provider.name}" to the platform?\n\n` +
        `Its key becomes a platform provider granted to every site, so usage is metered with AI credits. ` +
        `This site's own copy is turned off (not deleted). The key is copied on the server and never shown.`,
    );
    if (!ok) return;
    setMoving(provider.id);
    try {
      const res = await api.post<{ reusedExisting: boolean; sitesGranted: number }>(
        `/admin/platform/ai/site-providers/${provider.id}/move`,
      );
      addToast(
        res.reusedExisting
          ? `${provider.name} now runs on the platform's existing key`
          : `${provider.name} moved to the platform and granted to every site`,
        'success',
      );
      await loadProviders();
    } catch (err: any) {
      addToast(err.message || 'Failed to move the provider', 'error');
    } finally {
      setMoving(null);
    }
  }

  async function handleToggleActive(provider: AiProvider) {
    try {
      await api.put(`/admin/ai/providers/${provider.id}`, { is_active: !provider.is_active });
      await loadProviders();
    } catch {
      addToast('Failed to update provider', 'error');
    }
  }

  async function handleTestConnection(id: string) {
    setTestStatuses((s) => ({ ...s, [id]: 'testing' }));
    setTestErrors((s) => ({ ...s, [id]: '' }));
    try {
      const res = await api.post<{ ok: boolean; error?: string }>(`/admin/ai/providers/${id}/test`);
      setTestStatuses((s) => ({ ...s, [id]: res.ok ? 'success' : 'error' }));
      if (!res.ok) setTestErrors((s) => ({ ...s, [id]: res.error || 'Test failed' }));
    } catch {
      setTestStatuses((s) => ({ ...s, [id]: 'error' }));
      setTestErrors((s) => ({ ...s, [id]: 'Test request failed' }));
    }
  }

  // Drag-and-drop reorder (per capability)
  function handleDragStart(idx: number) {
    setDragIdx(idx);
  }

  function handleDragOver(e: React.DragEvent, idx: number) {
    e.preventDefault();
    setDragOverIdx(idx);
  }

  async function handleDrop(idx: number) {
    const tabProviders = getTabProviders();
    if (dragIdx === null || dragIdx === idx) {
      setDragIdx(null);
      setDragOverIdx(null);
      return;
    }
    const reordered = [...tabProviders];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(idx, 0, moved);
    setDragIdx(null);
    setDragOverIdx(null);

    // Optimistically update local state. Audio reorders write text_priority
    // (mirrors the backend's column reuse).
    const newProviders = providers.map((p) => {
      const newIdx = reordered.findIndex((r) => r.id === p.id);
      if (newIdx === -1) return p;
      const newPriority = (reordered.length - newIdx) * 10;
      return activeTab === 'image'
        ? { ...p, image_priority: newPriority }
        : { ...p, text_priority: newPriority };
    });
    setProviders(newProviders);

    try {
      await api.put('/admin/ai/providers/reorder', {
        capability: activeTab,
        ordered_ids: reordered.map((p) => p.id),
      });
    } catch {
      addToast('Failed to save new order', 'error');
      await loadProviders();
    }
  }

  const tabProviders = getTabProviders();

  if (loading) {
    return (
      <Card padding="lg">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card padding="lg">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-500" />
            <h3 className="text-lg font-semibold text-gray-900">AI Providers</h3>
          </div>
          <div className="flex items-center gap-2">
            {providers.length > 0 && (
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={recheckAll}
                title="Re-test every provider's connection"
              >
                <RefreshCw className="w-4 h-4 mr-1" /> Recheck keys
              </Button>
            )}
            <Button variant="primary" size="sm" type="button" onClick={openAddDialog}>
              <Plus className="w-4 h-4 mr-1" /> Add Provider
            </Button>
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          These are your site&rsquo;s own AI keys. AI features already work out of the box
          using the platform&rsquo;s shared keys &mdash; add your own keys here only if you
          want generation to run on your own provider account (your billing, your rate
          limits, your model choices). Your keys are always tried first.
        </p>

        {/* Tab bar */}
        <div className="flex border-b border-gray-200 mb-4">
          <button
            type="button"
            onClick={() => setActiveTab('text')}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'text'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Type className="w-4 h-4" />
            Text AI
            <span className="ml-1 text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">
              {providers.filter((p) => p.capabilities.includes('text')).length}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('image')}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'image'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Image className="w-4 h-4" />
            Image AI
            <span className="ml-1 text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">
              {providers.filter((p) => p.capabilities.includes('image')).length}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('audio')}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'audio'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Volume2 className="w-4 h-4" />
            Audio (TTS)
            <span className="ml-1 text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">
              {providers.filter((p) => p.capabilities.includes('audio')).length}
            </span>
          </button>
        </div>

        <p className="text-sm text-gray-500 mb-4">
          {activeTab === 'text' ? 'Text' : activeTab === 'image' ? 'Image' : 'Audio'} providers are tried in order from top to bottom.
          If one fails (rate limit, error), the next is automatically used.
        </p>

        {(() => {
          const fallbackTypes = fallbackTypesForActiveTab();
          const tenantTypes = new Set(tabProviders.map((p) => p.provider_type));
          const orphanFallbackTypes = [...fallbackTypes].filter((t) => !tenantTypes.has(t));
          const noConfigured = tabProviders.length === 0 && orphanFallbackTypes.length === 0;
          if (noConfigured) {
            const friendlyKind = activeTab === 'text' ? 'content generation' : activeTab === 'image' ? 'image generation' : 'text-to-speech voices';
            return (
              <div className="text-center py-8 text-gray-500">
                <p className="font-medium">No {activeTab} AI providers configured</p>
                <p className="text-sm mt-1">Add a provider to enable AI-powered {friendlyKind}.</p>
              </div>
            );
          }
          return null;
        })()}

        {(tabProviders.length > 0 || fallbackTypesForActiveTab().size > 0) && (
          <div className="space-y-2">
            {tabProviders.map((provider, idx) => {
              const models = (provider.model_config?.models || {}) as Record<string, string>;
              const currentModel = models[activeTab];
              const fallbackTypes = fallbackTypesForActiveTab();
              const hasPlatformFallback = fallbackTypes.has(provider.provider_type);

              return (
                <div
                  key={provider.id}
                  draggable
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                  onDrop={() => handleDrop(idx)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors ${
                    dragOverIdx === idx ? 'border-blue-400 bg-blue-50' :
                    !provider.is_active ? 'border-gray-200 bg-gray-50 opacity-60' :
                    'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <GripVertical className="w-4 h-4 text-gray-400 cursor-grab flex-shrink-0" />

                  <span className="text-xs font-medium text-gray-400 w-5 flex-shrink-0">{idx + 1}.</span>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                          testStatuses[provider.id] === 'success' ? 'bg-green-500'
                          : testStatuses[provider.id] === 'error' ? 'bg-red-500'
                          : testStatuses[provider.id] === 'testing' ? 'bg-amber-400 animate-pulse'
                          : 'bg-gray-300'
                        }`}
                        title={
                          testStatuses[provider.id] === 'success' ? 'Connection OK — key valid'
                          : testStatuses[provider.id] === 'error' ? `Broken: ${testErrors[provider.id] || 'connection failed'}`
                          : testStatuses[provider.id] === 'testing' ? 'Testing…'
                          : 'Not checked yet'
                        }
                        aria-label={`Key status: ${testStatuses[provider.id] || 'unknown'}`}
                      />
                      <span className="font-medium text-sm text-gray-900 truncate">{provider.name}</span>
                      <span className="text-xs text-gray-400">({provider.provider_type})</span>
                      {!provider.is_active && (
                        <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">Disabled</span>
                      )}
                      {hasPlatformFallback && (
                        <span
                          className="inline-flex items-center gap-1 text-xs bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded"
                          title="A platform-managed key for this provider type is also available — your key is tried first, the platform key is the backup."
                        >
                          <Shield className="w-3 h-3" />
                          Platform key as backup
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {currentModel && (
                        <span className="text-xs text-gray-500 font-mono">{currentModel}</span>
                      )}
                      {provider.has_api_key && (
                        <span className="text-xs text-gray-400">Key: {provider.api_key_masked}</span>
                      )}
                      {!provider.has_api_key && PROVIDER_META[provider.provider_type]?.requiresKey && (
                        <span className="text-xs text-amber-600">No API key</span>
                      )}
                      {provider.capabilities.length > 1 && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">
                          +{provider.capabilities.filter((c) => c !== activeTab).join(', ')}
                        </span>
                      )}
                      {/*
                        Zero is the value worth shouting about: the provider is
                        configured, keyed and active, and cannot serve a single
                        request. DeepSeek sat exactly here on a live tenant with
                        nothing on screen to say so. A healthy count is muted —
                        it is reassurance, not news.
                      */}
                      {provider.model_count === 0 ? (
                        <span
                          className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded"
                          title="The model registry holds no models for this provider, so it cannot serve a request. Usually the catalog fetch is failing — check the key, then re-save the provider to retry discovery."
                        >
                          <AlertTriangle className="w-3 h-3" />
                          No models
                        </span>
                      ) : typeof provider.model_count === 'number' ? (
                        <span className="text-xs text-gray-400">{provider.model_count} models</span>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {/* Test */}
                    <button
                      type="button"
                      onClick={() => handleTestConnection(provider.id)}
                      disabled={testStatuses[provider.id] === 'testing'}
                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                      title="Test connection"
                    >
                      {testStatuses[provider.id] === 'testing' && <Loader2 className="w-4 h-4 animate-spin" />}
                      {testStatuses[provider.id] === 'success' && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                      {testStatuses[provider.id] === 'error' && <XCircle className="w-4 h-4 text-red-500" />}
                      {(!testStatuses[provider.id] || testStatuses[provider.id] === 'idle') && <CheckCircle2 className="w-4 h-4" />}
                    </button>
                    {/* Toggle active */}
                    <button
                      type="button"
                      onClick={() => handleToggleActive(provider)}
                      className={`p-1.5 rounded ${provider.is_active ? 'text-green-600 hover:text-green-700' : 'text-gray-400 hover:text-gray-600'}`}
                      title={provider.is_active ? 'Disable' : 'Enable'}
                    >
                      {provider.is_active ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                    </button>
                    {/* Move to platform (platform admins only) */}
                    {isPlatformAdmin && provider.is_active && provider.has_api_key && (
                      <button
                        type="button"
                        onClick={() => handleMoveToPlatform(provider)}
                        disabled={moving === provider.id}
                        className="p-1.5 text-gray-400 hover:text-purple-600 rounded disabled:opacity-50"
                        title="Move to platform (metered)"
                        aria-label={`Move ${provider.name} to the platform`}
                      >
                        {moving === provider.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CloudUpload className="w-4 h-4" />}
                      </button>
                    )}
                    {/* Edit */}
                    <button
                      type="button"
                      onClick={() => openEditDialog(provider)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                      title="Edit"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    {/* Delete */}
                    <button
                      type="button"
                      onClick={() => handleDelete(provider.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Platform fallback rows for provider types the tenant has not configured itself.
                Read-only — tenants never see the key value or any platform-side identifier. */}
            {(() => {
              const fallbackTypes = fallbackTypesForActiveTab();
              const tenantTypes = new Set(tabProviders.map((p) => p.provider_type));
              const orphans = [...fallbackTypes].filter((t) => !tenantTypes.has(t));
              return orphans.map((providerType) => {
                const label = PROVIDER_TYPES.find((p) => p.value === providerType)?.label || providerType;
                return (
                  <div
                    key={`platform-fallback-${providerType}`}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-dashed border-purple-200 bg-purple-50/40"
                  >
                    <Shield className="w-4 h-4 text-purple-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-gray-900 truncate">{label}</span>
                        <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">
                          Powered by the platform
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        This works automatically using the platform&rsquo;s shared key. Add your
                        own {label} key above to run it on your own account instead.
                      </p>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        )}

        {/* Test error display */}
        {Object.entries(testErrors).filter(([, v]) => v).map(([id, err]) => (
          <div key={id} className="mt-2 text-xs text-red-600">
            {providers.find((p) => p.id === id)?.name}: {err}
          </div>
        ))}
      </Card>

      {/* Add/Edit Provider Modal — shared with the platform AI-providers section */}
      <AiProviderFormModal
        isOpen={showDialog}
        onClose={() => setShowDialog(false)}
        editing={editing}
        saving={saving}
        onSubmit={handleSubmit}
        titleAdd="Add AI Provider"
        titleEdit="Edit AI Provider"
      />
    </>
  );
}
